import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ItineraryService } from 'app/shared/itinerary.service';
import { NotificationService } from 'app/shared/notifications/notification.service';
import { UserService } from 'app/pages/com.usblick.common/auth/user.service';
import { RestService } from 'app/shared/rest.service';
import { ProductFactoryService } from 'services/itinerary/products/product-factory.service';
import { FlyProductService } from 'services/itinerary/products/fly-product.service';
import { IProductCreationParams } from 'model/product.interface';
import { FlightFareOption, FlightSegment, FlightSyncFareOptionsResponse } from 'model/fly.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { SeatMapComponent } from './seat-map/seat-map.component';
import { CurrencyConverterPurePipe } from 'app/shared/currency-converter-pure.pipe';
import { CarouselBreakpoint } from 'app/shared/utils/infinite-carousel';

interface TramoStep {
  type: 'tramo' | 'escala';
  segmentIndex?: number;
  label: string;
}

interface FareFeatureGroup {
  title: string;
  lines: string[];
}

const ORDINAL_LABELS = ['PRIMER TRAMO', 'SEGUNDO TRAMO', 'TERCER TRAMO', 'CUARTO TRAMO', 'QUINTO TRAMO'];

@Component({
  selector: 'usb-detail-modal-flight-result',
  templateUrl: './detail-modal-flight-result.component.html',
  styleUrls: ['./detail-modal-flight-result.component.scss'],
  providers: [CurrencyConverterPurePipe]
})
export class DetailModalFlightResultComponent implements OnInit {

  private readonly CATEGORY_ID = 5;

  flightData: any; // Datos del vuelo que recibirá
  payload: any; // Payload de búsqueda de vuelos
  arrayResults: any; // Respuesta completa de la búsqueda (searchId, etc)
  clientsSettingAppBooking: any;
  currencyAgency: any;
  currencies: any;

  // Modo "Por tramos": no crear el producto acá — sólo aplicar la tarifa
  // elegida y devolverla al componente que orquesta la selección tramo por
  // tramo, que decide cuándo (recién con todos los tramos elegidos) crear
  // los productos.
  deferCreation = false;

  @Output() productAdded = new EventEmitter<any>();
  @Output() fareSelectedForLeg = new EventEmitter<{ offer: any; fare: any }>();

  processing = false;
  selectedSegmentIndex = 0;

  // Igual patrón que shortcuts.component.ts: tramos tipo "responsive" de owl para el
  // usb-carousel de tarjetas de tarifa — 4 visibles en el ancho del modal (min-width 1240px,
  // ver .modal-lg-flight-detail), menos en pantallas chicas.
  fareCarouselBreakpoints: CarouselBreakpoint[] = [
    { minWidth: 0, items: 1 },
    { minWidth: 480, items: 2 },
    { minWidth: 768, items: 3 },
    { minWidth: 1100, items: 4 }
  ];

  // Familias tarifarias (baseFare + alternativeFares) traídas de /syncProducts
  fareOptions: FlightFareOption[] = [];
  loadingFareOptions = false;
  fareOptionsError = false;
  private chosenFareId: string | null = null;

  constructor(
    public bsModalRef: BsModalRef,
    private itineraryService: ItineraryService,
    private notificationService: NotificationService,
    private userService: UserService,
    private restService: RestService,
    private productFactory: ProductFactoryService,
    private flyProductService: FlyProductService,
    private modalService: BsModalService,
    private currencyPipe: CurrencyConverterPurePipe,
  ) { }

  ngOnInit(): void {
    this.loadFareOptions();
  }

  closeModal(): void {
    this.bsModalRef.hide();
  }

  // Mapa de asientos (solo visualización) de la tarifa que se está mostrando
  // ahora mismo — se abre como un modal aparte, encima de este.
  openSeatMap(): void {
    if (!this.flightData?.bookParam) {
      return;
    }

    this.modalService.show(SeatMapComponent, {
      initialState: {
        flightData: this.flightData,
        providerId: this.flightData?.providerId,
        bookingCode: this.flightData?.bookParam,
      },
      class: 'modal-lg modal-dialog-centered',
      backdrop: 'static',
      keyboard: false,
    });
  }

  // ==========================
  // Familias tarifarias (syncProducts / getFareOptions)
  // ==========================

  loadFareOptions(): void {
    if (!this.flightData) {
      return;
    }

    this.loadingFareOptions = true;
    this.fareOptionsError = false;

    const payload = this.flyProductService.buildFareOptionsPayload(this.flightData, this.payload, {
      userService: this.userService,
    });

    payload.products[0].raw_response = true;
    // payload.products[0].flights.optionIds = ["e9793129fd94eeae"];

    this.restService.doPost('app', 'syncProducts', payload).subscribe({
      next: (response: { success: boolean; mens: any }) => {

        const flightSync = response?.mens?.results?.find(r => r.category === this.CATEGORY_ID)?.data;

        const syncResult = flightSync?.results?.[0];

        this.fareOptions = this.buildFareOptionsList(syncResult?.fares);
        this.loadingFareOptions = false;
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error obteniendo familias tarifarias:', error);
        this.fareOptionsError = true;
        this.loadingFareOptions = false;
      }
    });
  }

  // La tarifa base (isBase: true) y alguna de las alternativas suelen
  // representar la MISMA tarifa (mismo cabin.fareBasis) expresada dos veces
  // — se descarta el duplicado para no mostrar dos tarjetas idénticas.
  private buildFareOptionsList(fares: FlightFareOption[]): FlightFareOption[] {
    const list = fares || [];
    const baseFare = list.find(fare => fare.isBase) || list[0];

    if (!baseFare) {
      return [];
    }

    const baseFareBasis = baseFare.cabin?.fareBasis;
    const others = list.filter(fare =>
      fare.fareId !== baseFare.fareId && (!baseFareBasis || fare.cabin?.fareBasis !== baseFareBasis)
    );

    return [baseFare, ...others];
  }

  getFareLabel(fare: FlightFareOption): string {
    return fare?.brandName || fare?.fareCode || 'Tarifa';
  }

  // Mismo criterio que BaseResultComponent.calculatePrice() (categoryId 5):
  // convierte el precio de la tarifa a la moneda de la agencia.
  getFarePrice(fare: FlightFareOption): number {
    return Number(this.currencyPipe.transform(
      fare?.price?.profility?.netPriceBooking,
      fare?.price?.currency,
      this.currencyAgency?.code,
      this.currencies,
    ));
  }

  isFareChosen(fare: FlightFareOption): boolean {
    return this.chosenFareId !== null && this.chosenFareId === fare?.fareId;
  }

  // Agrupa fare.services[] (HAND_BAG/CABIN_BAG/CHECKED_BAG/BAGGAGE ->
  // Equipaje, CHANGEABLE/REFUNDABLE -> Cambios y devoluciones) en secciones
  // para la tarjeta. Si por algún motivo no viene ningún service, se arma un
  // resumen a partir de rateClass/voidWindow.
  getFareFeatureGroups(fare: FlightFareOption): FareFeatureGroup[] {
    const services = fare?.services;

    if (!services || services.length === 0) {
      const lines = [
        fare?.rateClass === 'R' ? 'Tarifa reembolsable' : 'Tarifa no reembolsable',
      ];
      if (fare?.voidWindow?.description) {
        lines.push(fare.voidWindow.description);
      }
      return [{ title: 'Condiciones', lines }];
    }

    const baggageCodes = ['HAND_BAG', 'CABIN_BAG', 'CHECKED_BAG', 'BAGGAGE'];
    const conditionCodes = ['CHANGEABLE', 'REFUNDABLE'];

    const baggage = services.filter(s => baggageCodes.includes(s.providerCode || '')).map(s => s.name);
    const conditions = services.filter(s => conditionCodes.includes(s.providerCode || '')).map(s => s.name);
    const others = services
      .filter(s => !baggageCodes.includes(s.providerCode || '') && !conditionCodes.includes(s.providerCode || ''))
      .map(s => s.name);

    const groups: FareFeatureGroup[] = [];
    if (baggage.length) groups.push({ title: 'Equipaje', lines: baggage });
    if (conditions.length) groups.push({ title: 'Cambios y devoluciones', lines: conditions });
    if (others.length) groups.push({ title: 'Otros', lines: others });
    return groups;
  }

  // Aplica la tarifa elegida sobre la oferta original y recién ahí crea y
  // agrega el producto — así el item que queda en el itinerario reserva con
  // el bookingCode/precio de la tarifa realmente elegida.
  async chooseFare(fare: FlightFareOption | null): Promise<void> {
    if (this.processing || !this.flightData) {
      return;
    }

    this.chosenFareId = fare?.fareId ?? null;
    const offerToBook = fare ? this.flyProductService.applySelectedFare(this.flightData, fare) : this.flightData;

    if (this.deferCreation) {
      this.fareSelectedForLeg.emit({ offer: offerToBook, fare });
      this.closeModal();
      return;
    }

    await this.addFlightToItinerary(offerToBook);
  }

  // ==========================
  // Alta del producto
  // ==========================

  private async addFlightToItinerary(flightOffer: any): Promise<void> {
    this.processing = true;
    const loadingToast = this.notificationService.loading('Procesando producto...');
    try {
      const productParams: IProductCreationParams = {
        item: flightOffer,
        product: flightOffer,
        payload: this.payload,
        categoryId: this.CATEGORY_ID,
        othersPerCategory: {
          searchId: this.arrayResults?.searchId,
          userService: this.userService,
        },
      };

      const productService = this.productFactory.getProductService(this.CATEGORY_ID);
      const result = productService.createProduct(productParams);

      this.notificationService.close(loadingToast);

      if (!result) {
        this.notificationService.warning('No se pudo crear el producto');
        this.processing = false;
        return;
      }

      this.itineraryService.add(result).subscribe({
        next: (response: any) => {
          if (response) {
            this.itineraryService.evaluateDateItineraryRange(this.itineraryService.itinerary['items']);
            this.itineraryService.updateItinerary(this.itineraryService.itinerary);
            this.notificationService.success('¡Felicitaciones! Producto agregado con éxito.');
            this.productAdded.emit(result);
            this.closeModal();
          } else {
            this.notificationService.error('No se recibió confirmación del servidor', 'Error');
          }
          this.processing = false;
        },
        error: (error: HttpErrorResponse) => {
          console.error('Error adding product:', error);
          this.notificationService.error('Ocurrió un error al agregar el producto', 'Error');
          this.processing = false;
        }
      });

    } catch (error) {
      this.notificationService.close(loadingToast);
      console.error('Error al procesar:', error);
      this.notificationService.error('Ocurrió un error al procesar la solicitud');
      this.processing = false;
    }
  }

  // ==========================
  // Tramo / segmentos (sólo el tramo de ida — itinerary[0], igual que antes)
  // ==========================

  getAllSegments(): FlightSegment[] {
    return this.flightData?.itinerary?.[0]?.options?.[0]?.segments || [];
  }

  getSelectedSegment(): FlightSegment {
    return this.getAllSegments()[this.selectedSegmentIndex] || this.getAllSegments()[0];
  }

  getFirstSegment(): FlightSegment {
    return this.getAllSegments()[0];
  }

  getLastSegment(): FlightSegment {
    const segments = this.getAllSegments();
    return segments[segments.length - 1];
  }

  selectSegment(index: number): void {
    this.selectedSegmentIndex = index;
  }

  nextSegment(): void {
    if (this.selectedSegmentIndex < this.getAllSegments().length - 1) {
      this.selectedSegmentIndex++;
    }
  }

  hasNextSegment(): boolean {
    return this.selectedSegmentIndex < this.getAllSegments().length - 1;
  }

  // Arma la tira de pasos "PRIMER TRAMO / ESCALA / SEGUNDO TRAMO / ..." —
  // un paso "tramo" (seleccionable) por segmento, con un paso "escala" (sólo
  // informativo) entre cada par de segmentos consecutivos.
  getTramoSteps(): TramoStep[] {
    const segments = this.getAllSegments();
    const steps: TramoStep[] = [];

    segments.forEach((_segment, index) => {
      steps.push({
        type: 'tramo',
        segmentIndex: index,
        label: ORDINAL_LABELS[index] || `TRAMO ${index + 1}`,
      });
      if (index < segments.length - 1) {
        steps.push({ type: 'escala', label: 'ESCALA' });
      }
    });

    return steps;
  }

  getStopsLabel(): string {
    const stops = this.getAllSegments().length - 1;
    if (stops <= 0) return 'directo';
    return stops === 1 ? '1 escala' : `${stops} escalas`;
  }

  calculateLayover(index: number): number {
    const segments = this.getAllSegments();
    if (index >= segments.length - 1) return 0;

    const currentSegment = segments[index];
    const nextSegment = segments[index + 1];

    if (!currentSegment?.arrival?.dateTime || !nextSegment?.departure?.dateTime) return 0;

    const arrival = new Date(currentSegment.arrival.dateTime).getTime();
    const departure = new Date(nextSegment.departure.dateTime).getTime();

    return Math.floor((departure - arrival) / (1000 * 60)); // minutos
  }

  // ==========================
  // Formato
  // ==========================

  formatDuration(minutes: number): string {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  formatTime(dateTime: string): string {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: true });
  }

  getItem(item): void {
    console.log(item)
  }
}
